<script>
/* COMPOSANTS */
import MultiSelect from 'primevue/multiselect';
import AkCalendar from '@components/v2/input/AkCalendar';
import AkAutocomplete from '@components/v2/input/AkAutocomplete';
import AkButtonFooterSidebarFilter from '@components/v2/general/AkButtonFooterSidebarFilter.vue';

/* MIXINS */
import sidebarMixin from '@mixins/sidebarMixin';
import utilsMixin from '@mixins/utilsMixin';
import reservationSiteConst from '@mixins/const/reservationSiteConst';
import permissionsMixin from '@mixins/permissionsMixin';
import companyMixin from '@mixins/companyMixin';
import ownerAutocompleteMixin from '@mixins/autocomplete/ownerAutocompleteMixin';
import accommodationAutocompleteMixin from '@mixins/autocomplete/accommodationAutocompleteMixin';
import mandatAutocompleteMixin from '@mixins/autocomplete/mandatAutocompleteMixin';
import reservationAutocompleteMixin from '@mixins/autocomplete/reservationAutocompleteMixin';
import housekeeperAutocompleteMixin from '../../../mixins/autocomplete/housekeeperAutocompleteMixin';
import employeeAutocompleteMixin from '../../../mixins/autocomplete/employeeAutocompleteMixin';
import clientAutocompleteMixin from '@mixins/autocomplete/clientAutocompleteMixin';

/* SERVICES */

export default {
  components: {
    MultiSelect,
    AkCalendar,
    AkAutocomplete,
    AkButtonFooterSidebarFilter,
  },
  mixins: [
    utilsMixin,
    sidebarMixin,
    permissionsMixin,
    companyMixin,
    clientAutocompleteMixin,
    ownerAutocompleteMixin,
    accommodationAutocompleteMixin,
    employeeAutocompleteMixin,
    housekeeperAutocompleteMixin,
    mandatAutocompleteMixin,
    reservationAutocompleteMixin,
    reservationSiteConst,
  ],
  props: {
    modelValue: {required: true},
    callback: {
      type: Array,
      required: false,
      default: undefined,
    },
  },
  data() {
    return {
      ownerRelationManagerSearch: {
        roleType: 'GAS',
        roleName: 'OWNER_RELATION_MANAGER',
        userEnabled: true,
      },
    };
  },
  mounted() {},
  computed: {
    currentValue: {
      get: function () {
        return this.modelValue;
      },
      set: function (val) {
        this.$emit('update:modelValue', val);
      },
    },
    reservationSiteConstForFilter() {
      return this.reservationSiteConstDefinedReview;
    },
  },
  methods: {
    itemSelect(item) {
      this.$nextTick(() => {
        this.callback[0]();
      });
    },
    search() {
      this.callback[0]();
      this.hideSidebar();
    },
    reset() {
      if (this.$refs.ownerAutocomplete) this.$refs.ownerAutocomplete.reset();
      if (this.$refs.accommodationAutocomplete) this.$refs.accommodationAutocomplete.reset();
      if (this.$refs.reservationAutocomplete) this.$refs.reservationAutocomplete.reset();
      if (this.$refs.mandatAutocomplete) this.$refs.mandatAutocomplete.reset();
      if (this.$refs.housekeeperAutocomplete) this.$refs.housekeeperAutocomplete.reset();
      if (this.$refs.employeeAutocomplete) this.$refs.employeeAutocomplete.reset();

      this.callback[1]();
    },
  },
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent>
    <MultiSelect
      v-if="this.isGas() && this.companies"
      @change="itemSelect"
      v-model="currentValue.companyIds"
      :placeholder="$t('company.one')"
      :options="this.companies"
      option-label="name"
      option-value="id"
      class="form-control"
      inline />
    <AkAutocomplete
      v-if="!isOwner()"
      @valueChange="itemSelect"
      ref="ownerAutocomplete"
      v-model="currentValue.ownerId"
      :inline="true"
      :label="$t('owner.ownerDisplay')"
      option-value="id"
      option-label="display"
      @item-select="itemSelect"
      :force-selection="false"
      :init-method="this.autocompleteOwnerById"
      :search-method="this.autocompleteOwner"
      :search-object="this.autocompleteOwnerCustomer()"
      :search-label="this.autocompleteOwnerQuery" />

    <AkAutocomplete
      v-if="this.isGas() || this.isHousekeeper()"
      v-model="currentValue.customerId"
      :init-method="autocompleteClientById"
      inline
      :label="$t('activity.voyageur')"
      :search-method="autocompleteClient"
      @item-select="itemSelect"
      option-label="display"
      option-value="id" />

    <AkAutocomplete
      ref="accommodationAutocomplete"
      v-model="currentValue.accommodationId"
      :minLengthToLaunchSearch="3"
      :init-method="autocompleteAccommodationById"
      :placeholder="$t('accommodation.short')"
      :search-label="autocompleteAccommodationQuery"
      :search-method="autocompleteAccommodation"
      inline
      option-label="display"
      option-value="id"
      @item-select="itemSelect" />
    <AkAutocomplete
      ref="mandatAutocomplete"
      v-model="currentValue.mandatId"
      :inline="true"
      :label="$t('mandat.details')"
      option-value="id"
      option-label="num"
      @item-select="itemSelect"
      :force-selection="false"
      :init-method="this.autocompleteMandatById"
      :search-method="this.autocompleteMandat" />
    <AkAutocomplete
      ref="reservationAutocomplete"
      v-model="currentValue.reservationId"
      :inline="true"
      :minLengthToLaunchSearch="3"
      @item-select="itemSelect"
      label="Réservation"
      option-value="id"
      option-label="display"
      :init-method="this.autocompleteReservationById"
      :search-method="this.autocompleteReservation" />

    <AkAutocomplete
      v-if="this.isGas()"
      ref="housekeeperAutocomplete"
      v-model="currentValue.housekeeperId"
      :init-method="autocompleteHousekeeperById"
      :placeholder="$t('interlocutor.housekeeper')"
      :search-method="autocompleteHousekeeper"
      inline
      option-label="display"
      option-value="id"
      @item-select="itemSelect" />

    <AkAutocomplete
      v-if="this.isGas()"
      ref="employeeAutocomplete"
      v-model="currentValue.ownerRelationManagerId"
      :init-method="autocompleteEmployeeById"
      :placeholder="$t('role.owner_relation_manager')"
      :search-method="autocompleteEmployee"
      :searchObject="ownerRelationManagerSearch"
      inline
      option-label="display"
      option-value="id"
      @item-select="itemSelect" />
    <AkCalendar
      v-if="this.isGas() || this.isHousekeeper()"
      v-model="currentValue.dateCheckinPeriod"
      :inline="true"
      @valueChange="itemSelect"
      :placeholder="$t('reservation.periodCheckin')"
      date-format="dd/mm/yy"
      selection="range" />

    <AkCalendar
      v-if="this.isGas() || this.isHousekeeper()"
      v-model="currentValue.dateCheckoutPeriod"
      :inline="true"
      @valueChange="itemSelect"
      :placeholder="$t('reservation.periodCheckout')"
      date-format="dd/mm/yy"
      selection="range" />
    <AkCalendar
      @valueChange="itemSelect"
      v-model="currentValue.submittedAt"
      inline
      :placeholder="$t('review.submitted_at')"
      selection="range"
      :maxDate="new Date()" />
    <MultiSelect
      @change="itemSelect"
      v-model="currentValue.reservationSites"
      :placeholder="$t('reservation.reservation_site')"
      :options="reservationSiteConstForFilter"
      option-label="label"
      option-value="value"
      class="form-control"
      inline />

    <AkButtonFooterSidebarFilter :validateLabel="$t('search.search')" @action="search" @reset="reset" />
  </form>
</template>
